import React, { useMemo, useState } from 'react';
import Wrapper from 'components/layouts/Wrapper/Wrapper';
import { Headers, LoadingSpinner } from 'components/core';
import { Button } from 'components/core/Button/Button';
import ListEmpty from '../../Common/ListEmpty/ListEmpty';
import { useGetBuyerCancellationsQuery } from 'services/reportsApi';
import PropertyFilter from './PropertyFilter';
import CancellationsSummary from './CancellationsSummary';
import CancellationsTable from './CancellationsTable';
import { summarizeCancellations, uniqueProperties } from './summarize';
import { csvFilename, toCsv, triggerCsvDownload } from './csvExport';

export const CancellationsContainer: React.FC = () => {
  const { data, isLoading, isSuccess } = useGetBuyerCancellationsQuery();
  const [propertyId, setPropertyId] = useState<string | null>(null);

  const rows = data?.rows ?? [];
  const properties = useMemo(() => uniqueProperties(rows), [rows]);
  const filteredRows = useMemo(
    () => (propertyId ? rows.filter((r) => r.property_id === propertyId) : rows),
    [rows, propertyId]
  );
  const summary = useMemo(() => summarizeCancellations(filteredRows), [filteredRows]);

  const handleExport = () => {
    if (filteredRows.length === 0) return;
    triggerCsvDownload(toCsv(filteredRows), csvFilename());
  };

  return (
    <Wrapper additionalClasses="flex-col items-start" pageTitle="Cancellation Report">
      {isLoading && <LoadingSpinner />}
      <Headers headingContent="Cancellation Report" />
      <p className="text-14s text-gray-600">
        Payments canceled <strong>before</strong> they were sent to the vendor — refunded to your
        bank.
      </p>
      {isSuccess && rows.length === 0 && <ListEmpty subText="No cancellations yet." />}
      {isSuccess && rows.length > 0 && (
        <>
          <div className="flex w-full items-center justify-between">
            <PropertyFilter properties={properties} value={propertyId} onChange={setPropertyId} />
            <Button
              variants="extraSmall"
              buttonLabel="Export CSV"
              isDisabled={filteredRows.length === 0}
              onClick={handleExport}
              additionalClasses="h-9 px-4"
            />
          </div>
          <CancellationsSummary summary={summary} />
          {filteredRows.length === 0 ? (
            <ListEmpty subText="No cancellations for this property." />
          ) : (
            <CancellationsTable rows={filteredRows} />
          )}
        </>
      )}
    </Wrapper>
  );
};

export default CancellationsContainer;
